import { MedicationEntryType } from "model/medication-entry.model";
import { Dispatch } from "redux";
import {
  CONTACT_CREATE_START,
  CONTACT_CREATED,
  CONTACT_CREATED_ERROR,
  CONTACT_CURRENT,
  CONTACT_UPDATE,
  CONTACT_UPDATE_START,
  ContactAction,
} from "store/types/contact.types";
import { ContactType } from "model/contact.model";
import * as ContactAPI from "api/contact.api";
import { updateContact } from "api/contact.api";
import {
  selectCurrentContact,
  selectPatient,
} from "store/reducers/patient.reducer";
import { selectUser } from "store/reducers/user-context.reducer";
import { GlobalState } from "store/configure-store";
import { ThunkAction } from "redux-thunk";
import { createSubContactIfNeeded } from "store/actions/sub-contact.actions";
import * as MedicationActions from "store/actions/medication.actions";
import * as PrescriptionActions from "store/actions/prescription.actions";
import { ContactTypeEnum } from "common/enums/ContactTypeEnum";
import { StatusTypeEnum } from "common/enums/StatusTypeEnum";

/** ---- ACTIONS ---- */
export const createContactWithTarificationSession = (
  tSessionId: string
): ThunkAction<Promise<void>, GlobalState, any, ContactAction> => {
  return async (dispatch, getState) => {
    const state = getState();
    const patientId = selectPatient(state).id;
    const userId = selectUser(state).id;

    const currentContact = await dispatch(
      createContactIfNeeded(patientId, userId, ContactTypeEnum.HOUSEVISIT)
    );

    const toSaveContact = Object.assign({}, currentContact, {
      tarificationSessionId: tSessionId,
    });

    dispatchContactUpdateStart(toSaveContact)(dispatch);
    const newCurrentContact = await updateContact(
      patientId,
      userId,
      toSaveContact
    );
    dispatchContactUpdated(newCurrentContact)(dispatch);
  };
};

export const createContactWithPrescriptions = (
  medicationEntries: Array<MedicationEntryType>,
  canUseHelena: boolean
): ThunkAction<Promise<void>, GlobalState, any, ContactAction> => {
  return async (dispatch, getState) => {
    const state = getState();
    const patientId = selectPatient(state).id;
    const userId = selectUser(state).id;

    const contact = await dispatch(
      createContactIfNeeded(patientId, userId, ContactTypeEnum.PRESCRIPTION)
    );

    const subContact = await dispatch(
      createSubContactIfNeeded(patientId, userId, contact.id)
    );

    /** Stop Passive medication entries */
    const passiveEntries = medicationEntries.filter(
      (me) => me.status === StatusTypeEnum.NOT_RELEVANT
    );
    if (passiveEntries && passiveEntries.length > 0) {
      await dispatch(
        MedicationActions.stopMedicationEntries(
          patientId,
          userId,
          passiveEntries,
          subContact.id
        )
      );
    }

    /** Create prescription for all active entries */
    const activeEntries = medicationEntries.filter(
      (me) => me.status === StatusTypeEnum.ACTIVE
    );
    if (activeEntries && activeEntries.length > 0) {
      const newMedicationEntries = await dispatch(
        MedicationActions.createMedicationEntries(
          patientId,
          userId,
          activeEntries,
          subContact.id
        )
      );
      await dispatch(
        await PrescriptionActions.createPrescriptionAll(
          patientId,
          userId,
          newMedicationEntries,
          subContact.id,
          canUseHelena
        )
      );
    }

    return Promise.resolve();
  };
};

/** Create contact of type ContactType if no saved contact is found in the store
 *  Returns current store contact otherwise */
export const createContactIfNeeded = (
  patientId: number,
  caregiverId: number,
  contactType: ContactTypeEnum
): ThunkAction<Promise<ContactType>, GlobalState, any, ContactAction> => {
  return async (dispatch, getState) => {
    try {
      dispatchContactCreateStart()(dispatch);
      const storeContact = selectCurrentContact(getState());
      if (storeContact.id > 0) {
        dispatchUseCurrentContact(storeContact)(dispatch);
        return Promise.resolve(storeContact);
      } else {
        const currentContact = await ContactAPI.createEmptyContact(
          patientId,
          caregiverId,
          contactType
        );
        dispatchContactCreated(currentContact)(dispatch);
        return Promise.resolve(currentContact);
      }
    } catch (e) {
      dispatchContactCreatedError()(dispatch);
      throw e;
    }
  };
};

/** ---- DISPATCHERS ---- */
const dispatchContactCreateStart = () => {
  return (dispatch: Dispatch<ContactAction>) => {
    dispatch({ type: CONTACT_CREATE_START });
  };
};

const dispatchContactUpdateStart = (contact: ContactType) => {
  return (dispatch: Dispatch<ContactAction>) => {
    dispatch({ type: CONTACT_UPDATE_START, payload: contact });
  };
};

const dispatchContactCreated = (contact: ContactType) => {
  return (dispatch: Dispatch<ContactAction>) => {
    dispatch({ type: CONTACT_CREATED, payload: contact });
  };
};

const dispatchUseCurrentContact = (contact: ContactType) => {
  return (dispatch: Dispatch<ContactAction>) => {
    dispatch({ type: CONTACT_CURRENT, payload: contact });
  };
};

const dispatchContactUpdated = (contact: ContactType) => {
  return (dispatch: Dispatch<ContactAction>) => {
    dispatch({ type: CONTACT_UPDATE, payload: contact });
  };
};

const dispatchContactCreatedError = () => {
  return (dispatch: Dispatch<ContactAction>) => {
    dispatch({ type: CONTACT_CREATED_ERROR });
  };
};
